import {
  cloneElement,
  isValidElement,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@shared/lib/cn';
import { usePortalContainer } from '@shared/lib/portal';

import {
  POPOVER_ARROW,
  POPOVER_ARROW_CLASS,
  POPOVER_ARROW_LG_FILL_CLASS,
  POPOVER_ARROW_LG_STROKE_CLASS,
  POPOVER_ARROW_STROKE_WIDTH,
  POPOVER_INLINE_POSITION_CLASS,
  POPOVER_LAYOUT_CLASS,
  POPOVER_OFFSET_PX,
  POPOVER_SURFACE_CLASS,
  type PopoverArrow,
  type PopoverFrame,
  type PopoverPlacement,
  type PopoverStatus,
} from './popover.constant';
import { PopoverSkeleton } from './popover.skeleton.ui';

export interface PopoverProps {
  /** Figma `Content`. 상자 안의 내용(알림 목록 등) */
  content: ReactNode;
  /** 기준 요소(누르면 열리고 닫힌다). 없으면 상자만 그린다(inline 전용) */
  children?: ReactNode;
  /** Figma `placement`. 기준 요소의 어느 쪽에 뜨는지. 기본 top */
  placement?: PopoverPlacement;
  /** Figma `arrow`. sm = 16×8 칠한 꼭지, lg = 40×15 열린 V 선(알림 목록). 기본 sm */
  arrow?: PopoverArrow;
  /** Figma `status`. init = 막대 3줄 */
  status?: PopoverStatus;
  /** 상자 틀. none = 내용이 제 테두리를 가질 때(달력 · 시각 선택) 틀을 그리지 않는다. 기본 default */
  frame?: PopoverFrame;
  /** 제어 prop */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** 상자의 이름(보조기기). 보이는 제목이 없을 때 */
  'aria-label'?: string;
  /** portal 대신 제자리에 그린다(fe-rules 3절). 확인 페이지는 inline 으로 늘어놓는다 */
  inline?: boolean;
  className?: string;
}

interface Position {
  top: number;
  left: number;
}

function place(trigger: DOMRect, floating: DOMRect, placement: PopoverPlacement): Position {
  const gap = POPOVER_OFFSET_PX;
  switch (placement) {
    case 'top':
      return {
        top: trigger.top - floating.height - gap,
        left: trigger.left + (trigger.width - floating.width) / 2,
      };
    case 'bottom':
      return {
        top: trigger.bottom + gap,
        left: trigger.left + (trigger.width - floating.width) / 2,
      };
    case 'left':
      return {
        top: trigger.top + (trigger.height - floating.height) / 2,
        left: trigger.left - floating.width - gap,
      };
    case 'right':
      return {
        top: trigger.top + (trigger.height - floating.height) / 2,
        left: trigger.right + gap,
      };
  }
}

/**
 * 기준 요소를 누르면 옆에 뜨는 상자(알림 목록 등). 모달이 아니다 — 포커스를 가두지 않는다.
 * 다시 누르거나 · 바깥을 누르거나 · Esc 를 누르면 닫히고, Esc 는 기준 요소로 포커스를 돌려준다.
 */
export function Popover({
  content,
  children,
  placement = 'top',
  arrow = 'sm',
  status = 'default',
  frame = 'default',
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  'aria-label': ariaLabel,
  inline = false,
  className,
}: PopoverProps) {
  const id = useId();
  const [openState, setOpenState] = useState(defaultOpen);
  const open = openProp ?? openState;
  const setOpen = useCallback(
    (next: boolean) => {
      if (openProp === undefined) setOpenState(next);
      onOpenChange?.(next);
    },
    [openProp, onOpenChange],
  );

  const portalContainer = usePortalContainer();
  const triggerRef = useRef<HTMLSpanElement>(null);
  const floatingRef = useRef<HTMLDivElement>(null);
  const hasTrigger = children !== undefined;

  // portal 로 띄운 상자의 자리를 기준 요소에 맞춘다
  useLayoutEffect(() => {
    const trigger = triggerRef.current;
    const floating = floatingRef.current;
    if (inline || !open || !trigger || !floating) return;
    const { top, left } = place(
      trigger.getBoundingClientRect(),
      floating.getBoundingClientRect(),
      placement,
    );
    floating.style.top = `${top}px`;
    floating.style.left = `${left}px`;
    floating.style.visibility = 'visible';
  }, [inline, open, placement, content, status]);

  // 바깥을 누르면 닫는다(기준 요소가 있을 때만)
  useEffect(() => {
    if (!open || !hasTrigger) return;
    function handlePointerDown(event: PointerEvent) {
      const target = event.target as Node | null;
      if (!target) return;
      if (triggerRef.current?.contains(target) || floatingRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [open, hasTrigger, setOpen]);

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key !== 'Escape' || !open) return;
    event.stopPropagation();
    setOpen(false);
    triggerRef.current?.querySelector<HTMLElement>('button, a, [tabindex]')?.focus();
  }

  const shape = POPOVER_ARROW[arrow][placement];
  const box = (extraClass?: string) => (
    <div
      id={id}
      role="dialog"
      aria-label={ariaLabel}
      aria-busy={status === 'init' || undefined}
      data-placement={placement}
      data-arrow={arrow}
      data-status={status}
      onKeyDown={handleKeyDown}
      onClick={(event) => event.stopPropagation()}
      className={cn(
        'flex w-max items-center',
        POPOVER_LAYOUT_CLASS[placement],
        extraClass,
        className,
      )}
    >
      <div data-slot="surface" data-frame={frame} className={POPOVER_SURFACE_CLASS[frame]}>
        {status === 'init' ? <PopoverSkeleton /> : content}
      </div>
      <svg
        data-slot="arrow"
        aria-hidden="true"
        focusable="false"
        width={shape.width}
        height={shape.height}
        viewBox={`0 0 ${shape.width} ${shape.height}`}
        style={shape.style}
        className={POPOVER_ARROW_CLASS[arrow]}
      >
        {shape.fill ? <path d={shape.fill} className={POPOVER_ARROW_LG_FILL_CLASS} /> : null}
        <path
          d={shape.d}
          className={arrow === 'lg' ? POPOVER_ARROW_LG_STROKE_CLASS : undefined}
          strokeWidth={arrow === 'lg' ? POPOVER_ARROW_STROKE_WIDTH : undefined}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );

  if (!hasTrigger) return open ? box() : null;

  const trigger = isValidElement(children)
    ? cloneElement(
        children as ReactElement<{
          'aria-haspopup'?: string;
          'aria-expanded'?: boolean;
          'aria-controls'?: string;
        }>,
        {
          'aria-haspopup': 'dialog',
          'aria-expanded': open,
          'aria-controls': open ? id : undefined,
        },
      )
    : children;

  return (
    <span
      ref={triggerRef}
      className={cn('inline-flex', inline && 'relative')}
      onClick={() => setOpen(!open)}
      onKeyDown={handleKeyDown}
    >
      {trigger}
      {open && inline
        ? box(cn('absolute z-(--z-dropdown)', POPOVER_INLINE_POSITION_CLASS[placement]))
        : null}
      {open && !inline
        ? createPortal(
            <div ref={floatingRef} className="invisible fixed top-0 left-0 z-(--z-dropdown)">
              {box()}
            </div>,
            portalContainer,
          )
        : null}
    </span>
  );
}
